import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

const MOBILE_ITEMS_PER_PAGE = 4;

export default function TeamsSection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [data, setData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const res = await fetch("/competition/web/structure/");
        const json = await res.json();
        setData(Array.isArray(json) ? json : []);
      } catch {
        setData([]);
      }
    };

    loadTeams();
  }, []);

  const categories = useMemo(() => {
    return data.map((category) => ({
      id: category.id,
      name: category.name,
    }));
  }, [data]);

  useEffect(() => {
    if (!categories.length) {
      setSelectedCategory("");
      return;
    }

    const exists = categories.some(
      (category) => String(category.id) === String(selectedCategory)
    );

    if (!exists) {
      setSelectedCategory(String(categories[0].id));
    }
  }, [categories, selectedCategory]);

  const allTeams = useMemo(() => {
    return data.flatMap((category) =>
      (category.teams || []).map((team) => ({
        ...team,
        category_id: category.id,
        category_name: category.name,
      }))
    );
  }, [data]);

  const filteredTeams = useMemo(() => {
    if (!selectedCategory) {
      return [];
    }

    let base = allTeams.filter(
      (team) => String(team.category_id) === String(selectedCategory)
    );

    if (search.trim()) {
      const value = search.trim().toLowerCase();
      base = base.filter((team) => team.name.toLowerCase().includes(value));
    }

    return base.sort((a, b) => a.name.localeCompare(b.name));
  }, [allTeams, selectedCategory, search]);

  useEffect(() => {
    setPage(1);
  }, [selectedCategory, search, isMobile]);

  const totalPages = isMobile
    ? Math.ceil(filteredTeams.length / MOBILE_ITEMS_PER_PAGE)
    : 1;

  const visibleTeams = useMemo(() => {
    if (!isMobile) {
      return filteredTeams;
    }

    const start = (page - 1) * MOBILE_ITEMS_PER_PAGE;
    return filteredTeams.slice(start, start + MOBILE_ITEMS_PER_PAGE);
  }, [filteredTeams, isMobile, page]);

  return (
    <Box
      sx={{
        py: { xs: 2, md: 4 },
        background:
          "linear-gradient(180deg,#f8f8f9 0%,#ffffff 28%,#f9f9fa 100%)",
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            maxWidth: 1280,
            mx: "auto",
          }}
        >
          <Stack
            spacing={{ xs: 2, md: 3 }}
            alignItems="center"
            sx={{ mb: { xs: 2, md: 3 } }}
          >
            <Typography
              sx={{
                fontSize: { xs: "1.7rem", md: "2.3rem" },
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              Participating Institutions
            </Typography>

            <TextField
              placeholder="Search team by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: "100%",
                maxWidth: { xs: 260, sm: 420, md: 620 },
                "& .MuiOutlinedInput-root": {
                  height: { xs: 42, md: 56 },
                  borderRadius: "18px",
                  backgroundColor: "#fff",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Stack spacing={1.2} alignItems="center">
              <Typography
                sx={{
                  fontSize: "0.92rem",
                  fontWeight: 800,
                  color: "text.primary",
                  textAlign: "center",
                }}
              >
                Category
              </Typography>

              <Stack
                direction="row"
                spacing={1.2}
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
              >
                {categories.map((category) => (
                  <Button
                    key={category.id}
                    variant={
                      String(selectedCategory) === String(category.id)
                        ? "contained"
                        : "outlined"
                    }
                    onClick={() => setSelectedCategory(String(category.id))}
                    sx={{
                      minHeight: 42,
                      px: 2.6,
                      borderRadius: "999px",
                      fontWeight: 800,
                      textTransform: "none",
                    }}
                  >
                    {category.name}
                  </Button>
                ))}
              </Stack>
            </Stack>
          </Stack>

          {visibleTeams.length ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "stretch",
                  gap: { xs: 1.2, sm: 1.8, md: 3 },
                }}
              >
                {visibleTeams.map((team) => (
                  <Card
                    key={team.id}
                    elevation={0}
                    sx={{
                      width: {
                        xs: "calc(50% - 10px)",
                        sm: 260,
                        md: 280,
                      },
                      maxWidth: {
                        xs: 170,
                        sm: 260,
                        md: 300,
                      },
                      minWidth: {
                        xs: 145,
                        sm: 240,
                        md: 260,
                      },
                      borderRadius: { xs: "18px", md: "26px" },
                      border: "1.6px solid",
                      borderColor: "primary.main",
                      background:
                        "linear-gradient(180deg,#ffffff 0%,#fbfbfc 100%)",
                      transition: "transform .25s ease, box-shadow .25s ease",
                      px: { xs: 0.9, sm: 1.2, md: 1.6 },
                      py: { xs: 1.1, sm: 1.4, md: 1.8 },
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow:
                          "0 0 0 2px rgba(8,112,162,.16),0 18px 40px rgba(8,112,162,.22)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        gap: { xs: 0.8, sm: 1.1, md: 1.5 },
                      }}
                    >
                      <Box
                        component="img"
                        src={team.logo || ""}
                        alt={team.name}
                        sx={{
                          width: "100%",
                          maxWidth: { xs: 120, sm: 210, md: 250 },
                          height: { xs: 95, sm: 150, md: 185 },
                          objectFit: "contain",
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: { xs: ".88rem", sm: "1rem", md: "1.16rem" },
                          fontWeight: 800,
                          color: "primary.main",
                          lineHeight: 1.2,
                        }}
                      >
                        {team.name}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: ".76rem", sm: ".86rem", md: ".94rem" },
                          fontWeight: 800,
                          color: "#000",
                          lineHeight: 1.15,
                        }}
                      >
                        {team.category_name}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {isMobile && totalPages > 1 && (
                <Stack alignItems="center" sx={{ mt: 2.2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    size="small"
                  />
                </Stack>
              )}
            </>
          ) : (
            <Box
              sx={{
                borderRadius: "24px",
                border: "1px dashed",
                borderColor: "primary.main",
                backgroundColor: "#fff",
                py: { xs: 5, md: 7 },
                px: 2,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.08rem" },
                  fontWeight: 800,
                }}
              >
                No teams found
              </Typography>

              <Typography
                sx={{
                  fontSize: ".94rem",
                  color: "text.secondary",
                }}
              >
                Try another category or enter a different name
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}